import React, { Component } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import gql from 'graphql-tag';
import { withApollo } from 'react-apollo';
import * as Cookies from 'es-cookie';
import { ADM_MODAL_HEADER, ADM_MODAL_TITLE, ADM_MODAL_TITLE_SUB, ADM_MODAL_FOOTER, ADM_MODAL_HEADER_DOT } from '../admissionForms/admissionModalStyles';

// School identity shown on the printed form, sourced from the cookies set at login
// (institute name, complete address, logo URL). Overridable via the `school` prop.
const schoolFromCookies = () => ({
  name: Cookies.get('institutename') || Cookies.get('institutionname') || '',
  address: Cookies.get('address') || '',
  logo: Cookies.get('url') || '',
});

const GET_ADMISSION_VIEW = gql`
  query($edu_student_id: Int!) {
    getAdmissionViewDetails(edu_student_id: $edu_student_id) {
      student_id
      admission_no
      student_name
      class_name
      fields {
        field_id
        field_name
        field_key
        field_type_name
        is_base
        field_value
      }
    }
  }
`;

// Format a date value to dd/mm/yyyy. Handles "YYYY-MM-DD[...]", unix seconds/ms, and parseable dates.
const pad2 = (n) => String(n).padStart(2, '0');
const toDDMMYYYY = (value) => {
  if (value === null || value === undefined || value === '') return value;
  const v = String(value).trim();
  const iso = v.match(/^(\d{4})-(\d{2})-(\d{2})/); // YYYY-MM-DD (avoid timezone shifts)
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  if (/^\d+$/.test(v)) {
    const num = parseInt(v);
    const d = new Date(v.length > 10 ? num : num * 1000); // ms vs seconds
    if (!isNaN(d.getTime())) return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
  }
  const d = new Date(v);
  if (!isNaN(d.getTime())) return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
  return value;
};

// Render a field's value — date-of-birth / date-type fields are shown as dd/mm/yyyy.
const displayFieldValue = (f) => {
  const raw = f.field_value;
  if (raw === null || raw === undefined || raw === '') return '';
  const key = (f.field_key || '').toLowerCase();
  const name = (f.field_name || '').toLowerCase();
  const type = (f.field_type_name || '').toLowerCase();
  const isDate = type === 'date' || key.includes('dob') || key.includes('birth') || name.includes('birth') || name.includes('date of birth');
  return isDate ? toDDMMYYYY(raw) : raw;
};

// Print stylesheet used when opening the form in a standalone print window.
const PRINT_CSS = `
  * { box-sizing: border-box; font-family: 'Times New Roman', Georgia, serif; }
  body { margin: 10px; color: #000; }
  .adm-print-wrap { width: 100%; }
  table.adm-fields { width: 100%; border-collapse: collapse; }
  table.adm-fields td { border: 1px solid #000; padding: 3px 8px; font-size: 12px; vertical-align: top; }
  @page { size: A4; margin: 8mm; }
`;

// Read-only, printable view of one admission. Every field on the student's form (in
// order_no order), with values merged from edu_students (base) + admission_field_value.
class AdmissionViewModal extends Component {
  constructor(props) {
    super(props);
    this.state = { loading: true, error: '', details: null };
    this.printRef = React.createRef();
  }

  componentDidMount() {
    if (this.props.show && this.props.eduStudentId) this.fetchDetails();
  }

  componentDidUpdate(prevProps) {
    const opened = this.props.show && !prevProps.show;
    const changed = this.props.eduStudentId !== prevProps.eduStudentId;
    if (this.props.show && this.props.eduStudentId && (opened || changed)) {
      this.fetchDetails();
    }
  }

  fetchDetails = async () => {
    this.setState({ loading: true, error: '', details: null });
    try {
      const result = await this.props.client.query({
        query: GET_ADMISSION_VIEW,
        variables: { edu_student_id: parseInt(this.props.eduStudentId) },
        fetchPolicy: 'network-only',
      });
      const details = result.data.getAdmissionViewDetails;
      if (!details) {
        this.setState({ error: 'No admission details found for this student.', loading: false });
        return;
      }
      this.setState({ details, loading: false });
    } catch (err) {
      this.setState({ error: 'Failed to load admission details.', loading: false });
    }
  };

  handlePrint = () => {
    const node = this.printRef.current;
    if (!node) return;
    const win = window.open('', '_blank', 'width=900,height=1100');
    if (!win) return;
    win.document.write(
      `<html><head><title>Admission Form</title><style>${PRINT_CSS}</style></head>` +
      `<body><div class="adm-print-wrap">${node.innerHTML}</div></body></html>`
    );
    win.document.close();
    win.focus();
    // Let the new document lay out before printing.
    setTimeout(() => { win.print(); win.close(); }, 350);
  };

  render() {
    const { show, onHide } = this.props;
    const { loading, error, details } = this.state;
    const school = this.props.school || schoolFromCookies();
    const fields = details ? details.fields : [];

    const cellLabel = { border: '1px solid #000', padding: '3px 8px', width: '45%', fontSize: 12, verticalAlign: 'top' };
    const cellValue = { border: '1px solid #000', padding: '3px 8px', fontSize: 12, verticalAlign: 'top', fontWeight: 600 };

    return (
      <Modal show={show} onHide={onHide} centered size="lg">
        <Modal.Header closeButton style={ADM_MODAL_HEADER}>
          <Modal.Title style={ADM_MODAL_TITLE}>
            <span style={ADM_MODAL_HEADER_DOT} />
            <i className="fal fa-print" />
            <span>
              Admission Form
              <span style={ADM_MODAL_TITLE_SUB}>
                {details ? `${details.student_name || ''}${details.admission_no ? ' • Adm No ' + details.admission_no : ''}` : 'Printable admission record'}
              </span>
            </span>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ background: '#f1f5f9', padding: '20px', maxHeight: 'calc(100vh - 220px)', overflowY: 'auto' }}>
          {error && <div className="alert alert-danger py-2 small" style={{ borderRadius: 8, fontWeight: 600 }}>⛔ {error}</div>}

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" style={{ color: '#6366f1' }} />
              <p className="mt-2 text-muted small font-weight-bold">Loading admission details…</p>
            </div>
          ) : !details ? (
            !error && <div className="text-center py-5 text-muted">No details to show.</div>
          ) : (
            // The printable sheet — what you see here is what prints.
            <div ref={this.printRef} style={{ background: '#fff', padding: '16px 24px', margin: '0 auto', maxWidth: 820, color: '#000', fontFamily: "'Times New Roman', Georgia, serif", boxShadow: '0 1px 4px rgba(0,0,0,0.1)' }}>

              {/* Header: logo · school identity (from cookies) */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                {school.logo
                  ? <img src={school.logo} alt="logo" style={{ width: 72, height: 72, objectFit: 'contain', flexShrink: 0 }} />
                  : <div style={{ width: 72, height: 72, borderRadius: '50%', border: '2px solid #1e3a8a', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <i className="fas fa-school" style={{ fontSize: 28, color: '#1e3a8a' }} />
                    </div>}
                <div style={{ flex: 1, textAlign: 'center' }}>
                  <div style={{ fontSize: 26, fontWeight: 800, color: '#1e3a8a', letterSpacing: 0.5 }}>{school.name}</div>
                  {school.address ? <div style={{ fontSize: 13 }}>{school.address}</div> : null}
                </div>
              </div>

              <div style={{ textAlign: 'center', margin: '8px 0 10px' }}>
                <span style={{ fontSize: 18, fontWeight: 800, textDecoration: 'underline', letterSpacing: 1 }}>ADMISSION FORM</span>
              </div>

              {/* Fields, in order_no order, value from whichever table owns it */}
              <table className="adm-fields" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  {fields.map((f, i) => (
                    <tr key={`${f.field_key || ''}-${f.field_id || i}`}>
                      <td style={cellLabel}>{i + 1}. {f.field_name}</td>
                      <td style={cellValue}>{displayFieldValue(f)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Declaration */}
              <div style={{ fontSize: 13, marginTop: 10, lineHeight: 1.4 }}>
                <p style={{ margin: '0 0 6px' }}>
                  I declare that the statement given above is correct and I do not ask for any alteration,
                  including the Date of Birth of my son/daughter/ward in future.
                </p>
                <p style={{ margin: 0 }}>
                  I shall abide by the rules and regulations of the school in force from time to time.
                </p>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: 16, fontSize: 13 }}>
                <div>
                  <div>Place: Hyderabad</div>
                  <div style={{ marginTop: 6 }}>Date: __________________</div>
                </div>
                <div style={{ fontStyle: 'italic', fontWeight: 600 }}>Signature of the Parent/Guardian</div>
              </div>

              {/* Office use */}
              <div style={{ textAlign: 'center', fontWeight: 800, fontSize: 14, margin: '14px 0 8px' }}>(OFFICE USE)</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', fontSize: 13 }}>
                <div style={{ lineHeight: 1.6 }}>
                  <div>Admission No: <strong>{details.admission_no || '____________'}</strong></div>
                  <div>Admit in Class: <strong>{details.class_name || '__________________'}</strong></div>
                </div>
                <div style={{ fontStyle: 'italic', fontWeight: 600, alignSelf: 'flex-end' }}>Signature of the H.M.</div>
              </div>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer style={ADM_MODAL_FOOTER}>
          <Button variant="light" onClick={onHide} style={{ height: 40, borderRadius: 8, fontWeight: 600, padding: '0 24px' }}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={this.handlePrint}
            disabled={loading || !details}
            style={{ background: '#6366f1', border: 'none', height: 40, borderRadius: 8, fontWeight: 600, padding: '0 24px' }}
          >
            <i className="fas fa-print mr-2" /> Print
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

export default withApollo(AdmissionViewModal);
